import { PropTypes } from 'prop-types';
import { Dialog } from '@material-ui/core';
import { trI18n } from '@/common/utils/trI18n';
import { useTranslationSubscription } from '@/hooks/useTranslation';
import useAttachThumbnailData from '../handler/useAttachThumbnailData';
import useAttachThumbnailSize from '../handler/useAttachThumbnailSize';
import { THUMBNAIL_SIZE_CLIENT_CONTROL, resolveAttachIconSize } from '@/component/common/icons/AttachUtils';
import './AttachThumbnailModal.css';

// Extra width added to each grid column beyond the media, as a fraction of the thumbnail
// height, to account for the "more" trigger next to the media. Tuned by eye so the columns
// sit evenly without a hard-coded px; it scales with the `size` preset via thumbnailHeight.
const MORE_WIDTH_RATIO = 0.09;

/**
 * "View all attachments" modal. Display-only: it reads the shared hook's already-available
 * data and renders every attachment split into two sections (field attachments + chat-post
 * attachments). Opening it triggers no footprint/detail/navigation side effect.
 *
 * It reuses `useAttachThumbnailData` so preview/download/options/badges behave exactly like
 * the list, including the shared preview/logout dialogs rendered via `{dialogs}`.
 */
function AttachThumbnailModal({ ticketDetail, panelType, viewKey, options, attachBadge, projectInuseDetail, open, onClose }) {
    useTranslationSubscription();

    const thumbnailSize = useAttachThumbnailSize(THUMBNAIL_SIZE_CLIENT_CONTROL.TICKET_LIST_MODAL);
    // Derive the grid column width from the current thumbnail size so the CSS grid lays the
    // items out evenly from left to right without a hard-coded column width. The column width is
    // the real rendered thumbnail width: media (resolveAttachIconSize) + the "more" trigger.
    // The gap between items is a fixed value set in CSS (it does not scale with the size).
    const { width: mediaWidth, height: thumbnailHeight } = resolveAttachIconSize(thumbnailSize);
    const columnWidth = mediaWidth + Math.round(thumbnailHeight * MORE_WIDTH_RATIO);
    const gridStyle = { '--attach-grid-col': `${columnWidth}px` };

    const { ownAttachments, chatPostFiles, renderThumbnail, dialogs } = useAttachThumbnailData({
        ticketDetail,
        panelType,
        viewKey,
        isListContext: true,
        options,
        attachBadge,
        thumbnailSize,
        projectInuseDetail,
        onCloseModal: onClose
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <div className="attach-thumbnail-modal">
                <div className="attach-thumbnail-modal-body">
                    {ownAttachments.length > 0 && (
                        <div className="attach-thumbnail-modal-section">
                            <div className="attach-thumbnail-modal-section-title">
                                {trI18n('LABEL-FIELD-ATTACHMENT')}
                            </div>
                            <div className="attach-thumbnail-modal-grid" style={gridStyle}>
                                {ownAttachments.map((file, index) =>
                                    renderThumbnail(file, `own-${index}`)
                                )}
                            </div>
                        </div>
                    )}

                    {chatPostFiles.length > 0 && (
                        <div className="attach-thumbnail-modal-section">
                            <div className="attach-thumbnail-modal-section-title">
                                {trI18n('IMG_CHATPOST_FIELD')}
                            </div>
                            <div className="attach-thumbnail-modal-grid" style={gridStyle}>
                                {chatPostFiles.map((file, index) =>
                                    renderThumbnail(file, `chat-${index}`)
                                )}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {dialogs}
        </Dialog>
    );
}

AttachThumbnailModal.propTypes = {
    ticketDetail: PropTypes.any,
    panelType: PropTypes.any,
    viewKey: PropTypes.string,
    options: PropTypes.func,
    attachBadge: PropTypes.func,
    projectInuseDetail: PropTypes.any,
    open: PropTypes.bool,
    onClose: PropTypes.func
};

export default AttachThumbnailModal;
